import type { ReactNode } from 'react';
import { Spinner } from '@/shared/components/Loading/Spinner';

/**
 * Returns the spinner/error UI that should replace the whole chat surface,
 * or `null` when `ChatInterface` should proceed to render normally.
 *
 * A server-confirmed new conversation (spec ticket 10 §6.3) always proceeds
 * regardless of `isLoading`/`error` — there is nothing to load yet. Every
 * other case keeps today's behavior: spinner while loading, then 403/404
 * folded into "no messages yet" (unconfirmed-new fallback).
 */
export const getChatBlockingState = (
  isNewConversation: boolean,
  isLoading: boolean,
  error: unknown,
): ReactNode | null => {
  if (isNewConversation) return null;

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-full">
        <Spinner />
      </div>
    );
  }

  if (error) {
    const errorStatus = (error as any)?.statusCode;
    const isUnconfirmedNewConversation = errorStatus === 403 || errorStatus === 404;

    if (!isUnconfirmedNewConversation) {
      return (
        <div className="flex items-center justify-center h-full p-6 text-center">
          <div>
            <p className="text-red-600 mb-2">Failed to load conversation</p>
            <p className="text-sm text-gray-600">Please try again later</p>
          </div>
        </div>
      );
    }
    // For unconfirmed-new conversations (403/404), continue to render with
    // empty messages instead of returning error UI.
  }

  return null;
};
